import { ArrowRight, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBreadcrumbSchema, buildWebPageSchema } from '@/lib/seo/schemas';
import { useMemo } from 'react';
import { COMPANY } from '@/config/company';
import './AboutUs.css';

const SHOWROOM_IMAGE =
  'https://firebasestorage.googleapis.com/v0/b/mayimimarlik-6dfe5.firebasestorage.app/o/mayi-mimarlik-dis-cephe.webp?alt=media&token=b54ea0d7-becb-4e5e-a52b-10fec01a04c0';

const categories = [
  'Vitrifiye',
  'Seramik',
  'Granit',
  'Armatür',
  'Aksesuar',
  'Kimyasal',
];

export default function AboutUs() {
  useSEO({
    title: 'Hakkımızda | Mayi Mimarlık — Diyarbakır Seramik & Yapı Malzemeleri Distributörü',
    description:
      `${COMPANY.foundingYear}'den beri Diyarbakır Kayapınar'da faaliyet gösteren Mayi Mimarlık; Creavit, Duratiles, QUA Granite, Vitrafix ve Kalekim markalarının bölge distribütörüdür. Showroom: Çeysa Plaza.`,
    keywords: localKeywords(
      'mayi mimarlık hakkımızda',
      'diyarbakır seramik distribütör',
      'kayapınar yapı malzemeleri',
      'creavit bölge distribütörü diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/hakkimizda',
  });

  const schema = useMemo(() => {
    const page = buildWebPageSchema({
      name: 'Hakkımızda — Mayi Mimarlık',
      description: 'Diyarbakır merkezli seramik ve yapı malzemeleri distribütörü kurumsal bilgiler.',
      url: 'https://mayimimarlik.com/hakkimizda',
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'Hakkımızda', url: 'https://mayimimarlik.com/hakkimizda' },
    ]);
    const { '@context': _a, ...pg } = page;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [pg, crumb] };
  }, []);

  useStructuredData('schema-about', schema);

  return (
    <div className="about-page">
      <div className="about-inner">
        <header className="about-header">
          <h1>Hakkımızda</h1>
          <div className="about-header-accent" aria-hidden="true" />
        </header>

        <div className="about-grid">
          <div className="about-copy">
            <p>
              {COMPANY.foundingYear} yılından bu yana Diyarbakır&apos;da faaliyet gösteren firmamız,
              sektöründe öncü markaların bayiliğini üstlenerek kaliteli ve modern çözümler sunmaktadır.
            </p>
            <p>
              Creavit&apos;in şık vitrifiye ürünleri, Vitrafix&apos;in güvenilir yapı kimyasalları,
              Qua Granite&apos;in doğal doku serisi ve Duratiles Seramik&apos;in yenilikçi koleksiyonuyla
              projelerinize değer katıyoruz.
            </p>
            <p>
              Müşterilerimizin ihtiyaçlarını en iyi şekilde karşılamak ve yaşam alanlarına estetik,
              fonksiyonellik ile kaliteyi taşımak misyonumuzdur.
            </p>

            <Link to="/markalar" className="about-cta">
              Markalarımızı Keşfedin
              <ArrowRight size={18} />
            </Link>
          </div>

          <figure className="about-showcase">
            <div className="about-showcase-badge">
              <Award size={14} />
              Bölge Distribütörü
            </div>
            <div className="about-showcase-img-wrap">
              <img
                src={SHOWROOM_IMAGE}
                alt="Mayi Mimarlık showroom dış cephe — Creavit bölge distribütörü, Diyarbakır Kayapınar"
                width={960}
                height={720}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <figcaption className="about-showcase-caption">
                <strong>Mayi Mimarlık Showroom</strong>
                <span>Çeysa Plaza · Kayapınar / {COMPANY.city}</span>
              </figcaption>
            </div>
          </figure>
        </div>

        <section className="about-meta" aria-label="Ürün kategorileri">
          <p className="about-categories-label">Uzmanlık Alanlarımız</p>
          <div className="about-categories">
            {categories.map((cat) => (
              <div key={cat} className="about-category-pill">
                {cat}
              </div>
            ))}
          </div>
          <div className="about-distributor">
            <Award size={20} />
            Güneydoğu Anadolu Bölge Distribütörü
          </div>
        </section>
      </div>
    </div>
  );
}
